<script setup lang="ts">
import { stateOptions } from '~/utils/states'
import { proxyImageUrl } from '~/utils/imageProxy'
const props = defineProps<{
  brandColor: string
  brandName: string
  heroUrl?: string
  section?: 'overview' | 'salaries' | 'dress-code' | 'drug-test-policy' | 'hiring-process' | 'jobs' | 'reviews'
  selectedRole?: string
  selectedState?: string
  selectedCity?: string
  jobTitle?: string
  jobLocation?: { city?: string, state?: string }
}>()
const formatState = (stateCode: string) => {
  if (!stateCode) return ''
  if (stateCode.length > 2) return stateCode
  const state = stateOptions.find(
    s => s.code.toUpperCase() === stateCode.toUpperCase()
  )
  return state?.label || stateCode
}
const pageTitle = computed(() => {
  if (props.jobTitle) {
    let title = `${props.brandName} hiring ${props.jobTitle}`
    if (props.jobLocation?.city && props.jobLocation?.state) {
      title += `, ${props.jobLocation.city}, ${props.jobLocation.state}`
    } else if (props.jobLocation?.state) {
      title += `, ${props.jobLocation.state}`
    }
    return title
  }
  if (props.section === 'drug-test-policy') {
    return `Does ${props.brandName} Drug Test?`
  }
  if (props.section === 'hiring-process') {
    return `${props.brandName} Hiring Process`
  }
  if (props.section !== 'salaries' && props.section !== 'reviews') {
    switch (props.section) {
      case 'overview':
        return `Working at ${props.brandName}: Reviews, Salaries, and Jobs`
      case 'dress-code':
        return `${props.brandName} Dress Code: Guidelines for Employees`
      case 'jobs': {
        if (props.selectedCity && props.selectedState) {
          const stateFullName = formatState(props.selectedState)
          return `${props.brandName} Jobs in ${props.selectedCity}, ${stateFullName}`
        } else if (props.selectedState) {
          const stateFullName = formatState(props.selectedState)
          return `${props.brandName} Jobs in ${stateFullName}`
        }
        return `${props.brandName} Jobs`
      }
      default:
        return `Working at ${props.brandName}`
    }
  }
  const hasRole = Boolean(props.selectedRole?.trim())
  const hasState = Boolean(props.selectedState?.trim())
  const hasCity = Boolean(props.selectedCity?.trim())
  const hasLocation = hasState || hasCity
  let location = ''
  if (hasCity && hasState) {
    location = `${props.selectedCity}, ${props.selectedState.toUpperCase()}`
  } else if (hasState) {
    location = formatState(props.selectedState)
  } else if (hasCity) {
    location = props.selectedCity
  }
  if (props.section === 'salaries') {
    const currentYear = new Date().getFullYear()
    if (hasRole && hasLocation) {
      return `${props.brandName} ${props.selectedRole} Salaries in ${location} (${currentYear} Data)`
    }
    if (hasRole) {
      return `${props.brandName} ${props.selectedRole} Salaries in the U.S. (${currentYear} Data)`
    }
    if (hasLocation) {
      return `${props.brandName} Salaries in ${location} (${currentYear} Data)`
    }
    return `How Much Does ${props.brandName} Pay Employees in ${currentYear}?`
  } else {
    if (hasRole && hasLocation) {
      return `${props.brandName} ${props.selectedRole} Job Reviews in ${location}`
    }
    if (hasRole) {
      return `${props.brandName} ${props.selectedRole} Job Reviews`
    }
    if (hasLocation) {
      return `${props.brandName} Employee Reviews in ${location}`
    }
    return `${props.brandName} Employee Reviews`
  }
})
const safeColor = computed(() => {
  return props.brandColor || '#5F0CE2'
})
</script>
<template>
  <!-- Fixed min-height prevents CLS from responsive height changes during hydration -->
  <section class="relative min-h-[200px] py-8 overflow-hidden">
    <div class="absolute inset-0" :style="{
      backgroundColor: safeColor
        .replace(')', ', 0.2)')
        .replace('rgb', 'rgba')
    }" />
    <Container class="h-full relative z-10">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
        <div class="flex items-center justify-center">
          <div class="text-center">
            <h1 class="text-3xl md:text-5xl font-bold mb-4" :style="{ color: safeColor }">
              {{ pageTitle }}
            </h1>
          </div>
        </div>
        <div class="hidden md:flex items-center justify-center p-4">
          <div class="w-[400px] h-[200px] min-w-[400px] min-h-[200px] flex items-center justify-center">
            <img
              loading="eager"
              :src="proxyImageUrl(heroUrl) || 'data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27800%27 height=%27400%27%3E%3Crect fill=%27%23f3f4f6%27 width=%27100%25%27 height=%27100%25%27/%3E%3C/svg%3E'"
              :alt="`${brandName} hero image`"
              width="400"
              height="200"
              class="w-[400px] h-[200px] object-contain rounded-xl"
              decoding="async"
              fetchpriority="high"
            >
          </div>
        </div>
      </div>
    </Container>
  </section>
</template>
